import { EAT, VITALS } from "@eldrun/sim";

/** O que a HUD precisa dos atributos públicos (`Vitals`) e do corpo previsto. */
export interface VitalsSource {
  hp: number;
  hpMax: number;
  patua: number;
  patuaMax: number;
  hunger: number;
  hungerMax: number;
  patuaBroken: boolean;
  drowning: boolean;
  effect: string;
  effectLeft: number;
  dead: boolean;
}

export interface BodySource {
  stamina: number;
  staminaMax: number;
  exhausted: boolean;
  submerged: boolean;
  useTime: number;
}

export interface VitalsView {
  /** Frações de 0 a 1. */
  hp: number;
  patua: number;
  hunger: number;
  stamina: number;
  hpText: string;
  hungerText: string;
  exhausted: boolean;
  patuaBroken: boolean;
  submerged: boolean;
  drowning: boolean;
  /** Progresso do canal de comer (0 = não está comendo). */
  eating: number;
  effect: string | undefined;
  /** Vida < 40 %: vinheta; < 20 %: pulsando (doc 02 §19). */
  lowHp: boolean;
  criticalHp: boolean;
  lowHunger: boolean;
  starving: boolean;
  dead: boolean;
  /** Texto de quem morreu (como voltar, quem está assistindo). */
  deathMessage: string;
}

const EFFECT_LABELS: Record<string, string> = {
  regen: "Regenerando",
  saciedade: "Saciado",
  folego: "Fôlego extra",
  defesa: "Defesa reforçada",
};

const frac = (v: number, max: number) => (max > 0 ? Math.max(0, Math.min(1, v / max)) : 0);

/** Projeção pura para a HUD (testável sem DOM). */
/** O que a tela de morto mostra (F21): espera da Cruz, modo e quem está sendo assistido. */
export interface DeathInfo {
  wait: number;
  treta: boolean;
  spectating?: string | undefined;
  /** Sozinho na Irmandade (F54): a volta é paga com a própria bolsa, sem aliado. */
  solo?: boolean;
}

/** Texto de quem morreu: como voltar (Cruz de dia depois da espera, ou Carranca) e quem assiste. */
export function deathMessage(info: DeathInfo): string {
  const how = info.treta
    ? "Na Arena não há volta."
    : info.solo
      ? "Sozinho, você mesmo paga a Estátua da Fênix para voltar; desistir é derrota."
      : info.wait > 0
        ? `Um aliado pode te reviver na Lápide em ${info.wait} s (só de dia), ou pagar na Estátua da Fênix.`
        : "Um aliado pode te reviver na Lápide (só de dia), ou pagar na Estátua da Fênix.";
  const watching = info.spectating ? ` Assistindo ${info.spectating} · clique troca.` : "";
  return `Você morreu. ${how}${watching}`;
}

export function vitalsView(v: VitalsSource, body: BodySource, death?: DeathInfo): VitalsView {
  const effect = v.effect ? `${EFFECT_LABELS[v.effect] ?? v.effect} · ${v.effectLeft} s` : undefined;
  return {
    hp: frac(v.hp, v.hpMax),
    patua: frac(v.patua, v.patuaMax),
    hunger: frac(v.hunger, v.hungerMax),
    stamina: frac(body.stamina, body.staminaMax),
    hpText: `${Math.ceil(v.hp)}${v.patua > 0 ? ` +${Math.ceil(v.patua)}` : ""}`,
    hungerText: `${Math.ceil(v.hunger)}`,
    exhausted: body.exhausted,
    patuaBroken: v.patuaBroken,
    submerged: body.submerged,
    drowning: v.drowning,
    eating: body.useTime > 0 ? Math.min(1, body.useTime / EAT.seconds) : 0,
    effect,
    lowHp: !v.dead && v.hp < v.hpMax * 0.4,
    criticalHp: !v.dead && v.hp < v.hpMax * 0.2,
    lowHunger: v.hunger > 0 && v.hunger < VITALS.lowHunger,
    starving: v.hunger <= 0,
    dead: v.dead,
    deathMessage: v.dead ? deathMessage(death ?? { wait: 0, treta: false }) : "",
  };
}
